import React, { useEffect, useState, useRef, useCallback } from "react";
import { Footer, Navbar } from "../components";
import api from "../utils/base_url";
import { useNavigate } from "react-router-dom";
import "../styles/AboutPage.css";

/* ═══════════════════════════════════════════════════════════
   REVEAL HOOK
═══════════════════════════════════════════════════════════ */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

/* ═══════════════════════════════════════════════════════════
   SVG ICONS — Green + Terracotta palette
═══════════════════════════════════════════════════════════ */
const IconLeaf = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 56 56" fill="none">
    <circle cx="28" cy="28" r="28" fill="var(--primary-600)" />
    <path d="M28 10 C18 10,10 18,10 28 C10 38,18 46,28 46 C38 46,46 38,46 28 C46 18,38 10,28 10Z"
      fill="none" stroke="var(--text-inverse)" strokeWidth="2" />
    <line x1="28" y1="11" x2="28" y2="45" stroke="var(--text-inverse)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="28" y1="26" x2="21" y2="33" stroke="var(--text-inverse)" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="28" y1="33" x2="35" y2="40" stroke="var(--text-inverse)" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="28" cy="47" r="3" fill="var(--secondary-400)" />
  </svg>
);

const IconFounder = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 56 56" fill="none">
    <circle cx="28" cy="28" r="28" fill="var(--primary-600)" />
    <circle cx="28" cy="22" r="7" fill="var(--text-inverse)" />
    <path d="M14 44c0-7.732 6.268-14 14-14s14 6.268 14 14"
      stroke="var(--text-inverse)" strokeWidth="2.2" strokeLinecap="round" fill="none" />
    <circle cx="28" cy="10" r="3" fill="var(--secondary-400)" opacity="0.9" />
  </svg>
);

const IconSeedling = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="var(--primary-600)" />
    <path d="M20 10 C14 10,8 15,8 21 C8 27,14 32,20 32 C26 32,32 27,32 21 C32 15,26 10,20 10Z"
      fill="none" stroke="var(--text-inverse)" strokeWidth="1.8" />
    <line x1="20" y1="11" x2="20" y2="32" stroke="var(--text-inverse)" strokeWidth="1.3" strokeLinecap="round" />
    <line x1="20" y1="20" x2="15" y2="25" stroke="var(--text-inverse)" strokeWidth="1.1" strokeLinecap="round" />
    <line x1="20" y1="24" x2="25" y2="29" stroke="var(--text-inverse)" strokeWidth="1.1" strokeLinecap="round" />
    <circle cx="20" cy="34" r="2" fill="var(--secondary-400)" />
  </svg>
);

const IconOrganicIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="var(--primary-600)" />
    <path d="M20 10 L30 20 L20 30 L10 20 Z" stroke="var(--text-inverse)" strokeWidth="1.8" fill="none" />
    <circle cx="20" cy="20" r="4" fill="var(--secondary-400)" />
    <circle cx="20" cy="10" r="1.8" fill="var(--secondary-300)" />
    <circle cx="30" cy="20" r="1.8" fill="var(--secondary-300)" />
    <circle cx="20" cy="30" r="1.8" fill="var(--secondary-300)" />
    <circle cx="10" cy="20" r="1.8" fill="var(--secondary-300)" />
  </svg>
);

const IconShovel = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="var(--primary-600)" />
    <rect x="18" y="8" width="5" height="13" rx="2" fill="var(--text-inverse)" />
    <rect x="17" y="21" width="7" height="5" rx="1" fill="var(--secondary-400)" opacity="0.9" />
    <ellipse cx="20.5" cy="30" rx="5" ry="4" fill="var(--text-inverse)" />
  </svg>
);

const IconDelivery = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="var(--primary-600)" />
    <rect x="8" y="16" width="18" height="10" rx="2" fill="var(--text-inverse)" />
    <path d="M26 19 L32 19 L34 24 L26 24Z" fill="var(--text-inverse)" opacity="0.85" />
    <circle cx="13" cy="28" r="2.5" fill="var(--secondary-400)" />
    <circle cx="28" cy="28" r="2.5" fill="var(--secondary-400)" />
  </svg>
);

const IconWorkshop = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="var(--primary-600)" />
    <polygon points="20,10 32,16 20,22 8,16" fill="var(--text-inverse)" />
    <path d="M14 18 L14 27 Q14 30 20 30 Q26 30 26 27 L26 18"
      stroke="var(--text-inverse)" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <circle cx="20" cy="10" r="2" fill="var(--secondary-400)" />
  </svg>
);

/* Small pillar icons */
const PillarIcon = ({ emoji }) => (
  <div style={{
    width: 44, height: 44, borderRadius: "50%",
    background: "var(--primary-50)", border: "1.5px solid var(--primary-200)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "1.3rem", margin: "0 auto"
  }}>{emoji}</div>
);

/* Leaf bullet */
const LeafBullet = () => (
  <div className="about-mission-bullet">✦</div>
);

/* Decorative divider */
const SectionDivider = ({ center = false }) => (
  <div className="about-divider" style={center ? { justifyContent: "center" } : {}}>
    <div className="about-divider-line-l" />
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 1 C6 1,1 5,1 9 C1 13,5 17,9 17 C13 17,17 13,17 9 C17 5,13 1,9 1Z"
        fill="var(--about-gold)" opacity="0.3" />
      <circle cx="9" cy="9" r="3" fill="var(--about-gold)" />
    </svg>
    <div className="about-divider-line-r" />
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const AboutPage = () => {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const go = useCallback((id) => navigate("/product", { state: { categoryId: id } }), [navigate]);

  useEffect(() => {
    api.get("/categories/")
      .then(r => { if (r.data?.data) setCategories(r.data.data); })
      .catch(console.error);
  }, []);

  const repeated = [...categories, ...categories, ...categories];

  /* Reveal refs */
  const heroRef    = useReveal();
  const nameRef    = useReveal();
  const founderRef = useReveal();
  const diffRef    = useReveal();
  const missionRef = useReveal();
  const servRef    = useReveal();

  const pillars = [
    { emoji: "🌱", label: "Expert Horticulturists", desc: "Decades of hands-on nursery knowledge" },
    { emoji: "🌿", label: "100% Organic",           desc: "No harmful chemicals, ever" },
    { emoji: "🏡", label: "Farm Family Roots",       desc: "Grown from a true farming heritage" },
    { emoji: "💚", label: "Community First",          desc: "Trusted by thousands of homes" },
  ];

  const services = [
    { Icon: () => <IconSeedling />,    title: "Saplings & Seeds",        desc: "Wide variety of vegetable, flower, and fruit seedlings grown with care." },
    { Icon: () => <IconOrganicIcon />, title: "Organic Inputs",          desc: "Bio-fertilisers, vermicompost, and pesticide-free soil amendments." },
    { Icon: () => <IconDelivery />,    title: "Doorstep Delivery",       desc: "Fresh saplings delivered safely to your home across Tamil Nadu." },
    { Icon: () => <IconWorkshop />,    title: "Gardening Workshops",     desc: "Hands-on sessions for beginners — terrace, balcony, and kitchen gardens." },
  ];

  const badges = [
    { icon: "🌿", title: "Organic",    sub: "100% Chemical-Free" },
    { icon: "🤲", title: "Handgrown",  sub: "With Expert Care" },
    { icon: "🏆", title: "Trusted",    sub: "By 5000+ Families" },
    { icon: "🌍", title: "Eco-Minded", sub: "Sustainable Practices" },
  ];

  const missions = [
    "To bring <strong>green, living spaces</strong> into every home and community.",
    "To promote <strong>organic, chemical-free</strong> growing practices.",
    "To preserve <strong>heirloom and native plant</strong> varieties of Tamil Nadu.",
    "To educate and inspire the next generation of <strong>urban gardeners</strong>.",
  ];

  return (
    <>
      <Navbar />
      <main className="about-page">

        {/* ══════════ HERO ══════════ */}
        <section ref={heroRef} className="about-hero about-reveal">
          {/* Leaf-vein tiling pattern */}
          <svg aria-hidden className="about-hero-pattern" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="about-leaf-tile" x="0" y="0" width="72" height="72" patternUnits="userSpaceOnUse">
                <circle cx="36" cy="36" r="18" stroke="var(--primary-500)" strokeWidth="0.8" fill="none" />
                <circle cx="36" cy="36" r="10" stroke="var(--primary-400)" strokeWidth="0.5" fill="none" />
                <circle cx="36" cy="36" r="3" fill="var(--secondary-400)" opacity="0.5" />
                <line x1="36" y1="18" x2="36" y2="0"  stroke="var(--primary-400)" strokeWidth="0.6" />
                <line x1="36" y1="54" x2="36" y2="72" stroke="var(--primary-400)" strokeWidth="0.6" />
                <line x1="18" y1="36" x2="0"  y2="36" stroke="var(--primary-400)" strokeWidth="0.6" />
                <line x1="54" y1="36" x2="72" y2="36" stroke="var(--primary-400)" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#about-leaf-tile)" />
          </svg>

          <div className="about-hero-frame1" />
          <div className="about-hero-frame2" />

          {/* Corner diamonds */}
          {[
            { top: "14px", left: "14px" }, { top: "14px", right: "14px" },
            { bottom: "14px", left: "14px" }, { bottom: "14px", right: "14px" }
          ].map((pos, i) => (
            <svg key={i} width="14" height="14" viewBox="0 0 14 14" fill="none"
              style={{ position: "absolute", ...pos }}>
              <polygon points="7,0 14,7 7,14 0,7" fill="var(--secondary-400)" opacity="0.55" />
              <circle cx="7" cy="7" r="2" fill="var(--primary-100)" />
            </svg>
          ))}

          <div className="about-hero-content">
            <div className="about-hero-est">
              <div className="about-hero-est-line" />
              <p className="about-hero-est-text">Rooted in nature · Built with passion</p>
              <div className="about-hero-est-line" />
            </div>

            <div className="about-hero-logo-wrap">
              <img src="/mainlogo.png" alt="Saranya Bio Tech Nursery" className="about-hero-logo" />
            </div>

            <p className="about-hero-tagline">
              Saranya Bio Tech Nursery
            </p>
            <p className="about-hero-sub">
              From Tamil Nadu's fertile soil to your home — nurturing plants, nurturing lives.
            </p>

            <div className="about-hero-leaf-row">
              <div className="about-hero-leaf-line" />
              <IconLeaf size={24} />
              <div className="about-hero-leaf-line" />
            </div>
          </div>

          {/* Wave to dark section */}
          <svg aria-hidden className="about-hero-wave" viewBox="0 0 1440 56" preserveAspectRatio="none">
            <path d="M0,0 C360,56 1080,0 1440,56 L1440,56 L0,56Z" fill="var(--about-dark-bg)" />
          </svg>
        </section>

        {/* ══════════ BRAND NAME ══════════ */}
        <section ref={nameRef} className="about-dark-section about-reveal">
          <div className="about-dark-section-dot-bg" />
          <div className="about-section-inner" style={{ textAlign: "center", position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-4)" }}>
              <IconLeaf size={48} />
            </div>
            <p className="about-eyebrow light">The Story Behind the Name</p>
            <h2 className="about-section-title light">Why Saranya Bio Tech Nursery?</h2>
            <SectionDivider center />

            <div className="about-name-words">
              {[
                { word: "Saranya",  meaning: "Protector · Caretaker · Guide" },
                { word: "+",        meaning: "" },
                { word: "Bio Tech", meaning: "Science of Life · Organic Growth" },
              ].map((item, i) => (
                <div className="about-name-word" key={i}>
                  <div className={`about-name-word-text${item.word === "+" ? " plus" : ""}`}>
                    {item.word}
                  </div>
                  {item.meaning && (
                    <div className="about-name-word-meaning">{item.meaning}</div>
                  )}
                </div>
              ))}
            </div>

            <p className="about-body-text light" style={{ textAlign: "center" }}>
              Named after the <strong style={{ color: "var(--secondary-300)" }}>founder's belief</strong> that a good
              nursery protects and nurtures life — just as a caretaker does.{" "}
              <strong style={{ color: "var(--secondary-300)" }}>Bio Tech</strong> reflects our commitment to
              science-backed, organic methods rooted in{" "}
              <strong style={{ color: "var(--secondary-300)" }}>Tamil Nadu's farming traditions</strong>.
            </p>
          </div>
        </section>

        {/* ══════════ FOUNDER ══════════ */}
        <section ref={founderRef} className="about-light-section about-reveal">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-4)" }}>
            <IconFounder size={48} />
          </div>
          <p className="about-eyebrow dark" style={{ textAlign: "center" }}>Founder's Corner</p>
          <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
            The Vision Behind the Nursery
          </h2>
          <SectionDivider center />

          <div className="about-founder-card">
            {/* Photo panel */}
            <div className="about-founder-left">
              <div className="about-founder-img-wrap">
                <img src="./assets/founder.jpeg" alt="Saranya — Founder" />
              </div>
              <div className="about-founder-ring" />
              <div className="about-founder-name-wrap">
                <p className="about-founder-name">Saranya</p>
                <p className="about-founder-role">Founder &amp; Head Horticulturist</p>
              </div>
            </div>

            {/* Quote panel */}
            <div className="about-founder-right">
              <div className="about-founder-open-quote">"</div>
              <p className="about-founder-quote">
                I started this nursery with a single dream — that every home in Tamil Nadu should have
                at least one <strong>green, living plant</strong>. From our farm to your doorstep,
                every sapling carries the <strong>love, knowledge, and hope</strong> we pour into it.
                This is not just a business; it is a{" "}
                <strong style={{ color: "var(--primary-600)" }}>mission to make India greener</strong>,
                one plant at a time.
              </p>
              <div className="about-founder-sig-row">
                <div className="about-founder-sig-line" />
                <span className="about-founder-sig">Saranya</span>
                <span style={{ color: "var(--secondary-400)", fontSize: "1rem" }}>🌿</span>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════ WHAT MAKES US DIFFERENT ══════════ */}
        <section ref={diffRef} className="about-light-section about-reveal"
          style={{ background: "var(--primary-50)", maxWidth: "100%", paddingInline: "var(--space-6)" }}>
          <div style={{ maxWidth: "960px", margin: "0 auto" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-4)" }}>
              <IconShovel size={48} />
            </div>
            <p className="about-eyebrow dark" style={{ textAlign: "center" }}>Our Difference</p>
            <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
              What Makes Us Different?
            </h2>
            <SectionDivider center />

            <div className="about-diff-box">
              <p className="about-diff-box-title">
                Built from a Farmer's Real Experience
              </p>
              <p className="about-body-text dark">
                Before founding <strong>Saranya Bio Tech Nursery</strong>, our journey began as
                farmers who struggled to find truly <strong>healthy, chemical-free saplings</strong>{" "}
                in the market. The confusion, the disappointment, and the lack of trustworthy sources
                became the spark that ignited this nursery.
              </p>
              <p className="about-body-text dark" style={{ marginBottom: 0 }}>
                Every plant at <strong>Saranya Bio Tech</strong> is grown with the promise that
                customers will never face those same struggles — only{" "}
                <strong>genuine quality, honest care, and living green beauty</strong> with every purchase.
              </p>
            </div>

            <div className="about-pillars-grid">
              {pillars.map(({ emoji, label, desc }, i) => (
                <div key={i} className="about-pillar-card">
                  <div className="about-pillar-icon">
                    <PillarIcon emoji={emoji} />
                  </div>
                  <p className="about-pillar-label">{label}</p>
                  <p className="about-pillar-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ MISSION ══════════ */}
        <section ref={missionRef} className="about-dark-section about-reveal"
          style={{ textAlign: "center" }}>
          <div className="about-dark-section-dot-bg" />
          <div className="about-section-inner" style={{ position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-4)" }}>
              <IconLeaf size={48} />
            </div>
            <p className="about-eyebrow light">Our Mission</p>
            <h2 className="about-section-title light">
              Growing Green, Growing Together
            </h2>
            <SectionDivider center />

            <div className="about-mission-list">
              {missions.map((text, i) => (
                <div key={i} className="about-mission-item">
                  <LeafBullet />
                  <p className="about-mission-text"
                    dangerouslySetInnerHTML={{
                      __html: text.replace(/<strong>/g, '<strong style="color:var(--secondary-300)">')
                    }} />
                </div>
              ))}
            </div>

            <p className="about-mission-closing">
              We believe every home deserves to be a little greener. 🌿
            </p>
          </div>
        </section>

        {/* ══════════ SERVICES ══════════ */}
        <section ref={servRef} className="about-light-section about-reveal">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-4)" }}>
            <IconShovel size={48} />
          </div>
          <p className="about-eyebrow dark" style={{ textAlign: "center" }}>What We Offer</p>
          <h2 className="about-section-title dark" style={{ textAlign: "center" }}>Our Services</h2>
          <SectionDivider center />

          <div className="about-services-grid">
            {services.map(({ Icon, title, desc }, i) => (
              <div key={i} className="about-service-card">
                <div className="about-service-icon"><Icon /></div>
                <p className="about-service-title">{title}</p>
                <p className="about-service-desc">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════ TRUST BADGES ══════════ */}
        <div className="about-badges-strip">
          <div className="about-badges-grid">
            {badges.map(({ icon, title, sub }, i) => (
              <div key={i} className="about-badge-item">
                <div className="about-badge-icon">{icon}</div>
                <p className="about-badge-title">{title}</p>
                <p className="about-badge-sub">{sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════ CATEGORY MARQUEE ══════════ */}
        {categories.length > 0 && (
          <section className="about-collections-section">
            <div className="about-collections-head">
              <p className="about-eyebrow dark" style={{ textAlign: "center" }}>Explore</p>
              <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
                Our Collections
              </h2>
              <SectionDivider center />
            </div>

            <div className="about-marquee-wrap">
              <div className="about-marquee">
                {repeated.map((cat, i) => (
                  <div key={i} className="about-cat-card" onClick={() => go(cat.id)}>
                    <div className="about-cat-img">
                      <img
                        src={cat.category_image || "https://via.placeholder.com/300x200"}
                        alt={cat.category_name}
                      />
                    </div>
                    <div className="about-cat-label">
                      <span>{cat.category_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </main>
      <Footer />
    </>
  );
};

export default AboutPage;